import React, { useState, useEffect, useRef } from "react";
import {
  Edit,
  Trash2,
  Copy,
  Eye,
  Calendar,
  FileSpreadsheet,
  MoreVertical,
} from "lucide-react";

interface TemplatesGridProps {
  templates: any[];
  searchTerm: string;
  onEditTemplate: (template: any) => void;
  onDeleteTemplate: (template: any) => void;
  onPreviewTemplate: (template: any) => void;
  onDuplicateTemplate: (template: any) => void;
  isLoading: boolean;
}

const TemplatesGrid: React.FC<TemplatesGridProps> = ({
  templates,
  searchTerm,
  onEditTemplate,
  onDeleteTemplate,
  onPreviewTemplate,
  onDuplicateTemplate,
  isLoading,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredTemplates = (Array.isArray(templates) ? templates : []).filter(
    (template) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        template.name?.toLowerCase().includes(searchLower) ||
        template.subject?.toLowerCase().includes(searchLower) ||
        template.content?.toLowerCase().includes(searchLower)
      );
    }
  );

  const handleMenuToggle = (templateId: string) => {
    setOpenMenuId((prev) => (prev === templateId ? null : templateId));
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  if (filteredTemplates.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <div className="bg-blue-500 p-3 rounded-lg w-12 h-12 mx-auto flex items-center justify-center shadow-sm">
            <FileSpreadsheet className="h-5 w-5 text-white" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {searchTerm ? "No templates found" : "No templates yet"}
        </h3>
        <p className="text-gray-500">
          {searchTerm
            ? "Try adjusting your search criteria"
            : "Get started by creating your first email template"}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template, index) => {
          // Use the actual template ID from the database and ensure it's a string
          const templateId = String(
            template.id || template._id || `template-${index}`
          );

          return (
            <div
              key={templateId}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="bg-blue-500 p-2 rounded-lg shadow-sm">
                    <FileSpreadsheet className="h-4 w-4 text-white" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {template.name}
                    </h3>
                    <p className="text-sm text-gray-500">{template.subject}</p>
                  </div>
                </div>

                <div className="relative" ref={menuRef}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMenuToggle(templateId);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="More options"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>

                  {openMenuId === templateId && (
                    <div
                      className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px] z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAction(() => {
                            onPreviewTemplate(template);
                          });
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </button>
                      <button
                        onClick={() =>
                          handleAction(() => onDuplicateTemplate(template))
                        }
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Duplicate
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAction(() => {
                            onEditTemplate(template);
                          });
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAction(() => {
                            onDeleteTemplate(template);
                          });
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 line-clamp-3">
                  {template.content?.substring(0, 120)}...
                </p>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {new Date(template.createdAt).toLocaleDateString()}
                </div>
                <span className="bg-gray-100 px-2 py-1 rounded">
                  {template.type || "Custom"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TemplatesGrid;
